import { tryToDispose, tryToMarkDispose } from '@tencent/dispose-tree';
import { SyncDescriptor } from './descriptors';
import { ServiceHolder } from './typing';
import { getIdentifierName } from './service-identifier';
import { KeyMapping } from './key-mapping';
import { AMBIGUOUS_MATCH_SERVICE, OVERRIDE_WARNING, UPDATE_HOLDER_INSTANCE } from '../constants/error-messages';
/**
 * DI框架的核心容器
 */
export class ServiceCollection {
    constructor(...entries) {
        // 保存模块的ID和Holder的容器
        this.innerEntries = new Map();
        this.keyMapping = new KeyMapping();
        for (const [id, service] of entries) {
            this.set(id, service);
        }
    }
    /**
     * 设置id对应的service,多次设置时根据一定的策略覆盖
     * @param id
     * @param instanceOrDescriptor
     * @returns
     */
    set(id, instanceOrDescriptor) {
        const key = this.keyMapping.generateServiceKey(id, instanceOrDescriptor);
        const holder = this.innerEntries.get(key);
        // holder为空时新建Holder
        if (!holder) {
            this.createNewServiceHolder(key, instanceOrDescriptor);
            return;
        }
        if (instanceOrDescriptor instanceof SyncDescriptor) {
            // 根据一定的策略进行override
            this.overrideExistServiceHolder(id, holder, instanceOrDescriptor);
        }
        else {
            // holder存在时设置instance
            this.setHolderInstance(key, holder, instanceOrDescriptor);
        }
    }
    /**
     * 设置id对应的service,多次设置时会绑定多个service
     * @param id
     * @param instanceOrDescriptor
     */
    setMulti(id, instanceOrDescriptor) {
        const key = this.keyMapping.generateServiceKey(id, instanceOrDescriptor, true);
        const holder = this.innerEntries.get(key);
        // holder为空时新建Holder
        if (!holder) {
            const newHolder = this.createNewHolderByCondition(instanceOrDescriptor);
            this.innerEntries.set(key, newHolder);
        }
    }
    /**
     * 判断是有对应id的binding
     * @param id
     * @returns
     */
    has(id) {
        const keys = this.keyMapping.getMapping(id);
        return !!keys.find((key) => this.innerEntries.has(key));
    }
    /**
     * @deprecated The method should not be used
     */
    get(id) {
        const keys = this.keyMapping.getMapping(id);
        if (keys.length === 0) {
            return undefined;
        }
        if (keys.length > 1) {
            // eslint-disable-next-line new-cap
            throw new Error(AMBIGUOUS_MATCH_SERVICE(getIdentifierName(id)));
        }
        const holder = this.innerEntries.get(keys[0]);
        return (holder === null || holder === void 0 ? void 0 : holder.instance) || (holder === null || holder === void 0 ? void 0 : holder.descriptor) || undefined;
    }
    /**
     * 获取一个query对应的所有holder
     * @param query
     * @param filterCallback
     * @returns
     */
    getAllHolder(query, filterCallback) {
        const keys = this.keyMapping.getMapping(query.serviceIdentifier);
        const holders = [];
        keys.forEach((key) => {
            const holder = this.innerEntries.get(key);
            if (holder) {
                holders === null || holders === void 0 ? void 0 : holders.push(holder);
            }
        });
        if (filterCallback) {
            return holders === null || holders === void 0 ? void 0 : holders.filter((holder, idx) => filterCallback(holder, idx));
        }
        return holders.length > 0 ? holders : undefined;
    }
    /**
     * 获取一个query对应的唯一holder, 但存在多个holder时抛出异常
     * @param query
     * @returns
     */
    getHolder(query) {
        const keys = this.keyMapping.getMapping(query.serviceIdentifier);
        if (keys.length === 0) {
            return undefined;
        }
        if (keys.length > 1) {
            // eslint-disable-next-line new-cap
            throw new Error(AMBIGUOUS_MATCH_SERVICE(getIdentifierName(query.serviceIdentifier)));
        }
        const holder = this.innerEntries.get(keys[0]);
        return holder;
    }
    getMapping() {
        return this.keyMapping;
    }
    /**
    * 清理容器中的实例
    * @param shouldDispose
    * @param shouldReset
    * @param shouldDelete
    * @param trace
    */
    teardown(shouldDispose, shouldReset, shouldDelete, trace) {
        // 清理容器中的对象实例
        this.innerEntries.forEach((holder, key) => {
            const { instance, descriptor } = holder;
            if (shouldDispose === null || shouldDispose === void 0 ? void 0 : shouldDispose(instance)) {
                tryToDispose(instance, trace === null || trace === void 0 ? void 0 : trace.branch(getIdentifierName(key)));
            }
            if (descriptor && instance) {
                if (shouldReset === null || shouldReset === void 0 ? void 0 : shouldReset(instance)) {
                    holder.instance = null;
                }
            }
            if (!descriptor && instance) {
                if (shouldDelete === null || shouldDelete === void 0 ? void 0 : shouldDelete(instance)) {
                    this.innerEntries.delete(key);
                    this.keyMapping.deleteMapping(key);
                }
            }
        });
    }
    createNewServiceHolder(serviceKey, instanceOrDescriptor) {
        // 容器中可能是实例，也可能是SyncDescriptor
        const holder = this.createNewHolderByCondition(instanceOrDescriptor);
        this.innerEntries.set(serviceKey, holder);
    }
    overrideExistServiceHolder(identifier, holder, descriptor) {
        var _a, _b, _c;
        // supportsOverride模式下才支持覆盖
        if ((_a = holder.descriptor) === null || _a === void 0 ? void 0 : _a.supportsOverride) {
            // 生成新的key
            const newServiceKey = this.keyMapping.generateServiceKey(identifier, descriptor);
            const newHolder = this.createHolderInstance(null, descriptor);
            this.innerEntries.set(newServiceKey, newHolder);
        }
        else if (!holder.instance) {
            if (!((_b = holder.descriptor) === null || _b === void 0 ? void 0 : _b.isEqualWith(descriptor))) {
                // 多个key绑定到同一个descriptor的情况下无效warning
                // 同一个key绑定到多个descriptor的情况下需要warning
                // eslint-disable-next-line new-cap
                console.warn(OVERRIDE_WARNING(getIdentifierName(identifier)));
            }
        }
        else {
            // 原有holder有实例时，更新构造函数，抛出异常
            // eslint-disable-next-line new-cap
            throw new Error(UPDATE_HOLDER_INSTANCE(getIdentifierName(identifier), (_c = holder.descriptor) === null || _c === void 0 ? void 0 : _c.ctor.name));
        }
    }
    setHolderInstance(serviceKey, holder, instance) {
        tryToMarkDispose(instance);
        // 可能覆盖原holder里的实例
        holder.instance = instance;
        this.innerEntries.set(serviceKey, holder);
    }
    createNewHolderByCondition(instanceOrDescriptor) {
        // 容器中可能是实例，也可能是SyncDescriptor
        if (instanceOrDescriptor instanceof SyncDescriptor) {
            const holder = this.createHolderInstance(null, instanceOrDescriptor);
            return holder;
        }
        // 放入的实例需要track
        tryToMarkDispose(instanceOrDescriptor);
        const holder = this.createHolderInstance(instanceOrDescriptor, null);
        return holder;
    }
    createHolderInstance(instance, descriptor) {
        const holder = new ServiceHolder(instance, descriptor);
        return holder;
    }
}
//# sourceMappingURL=service-collection.js.map